import { Alert, Switch } from "@mui/material";
import { Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router";

import { useAnalytics, useAuth, useNavigation } from "@/providers";
import { AlertDialog, RobotoTooltip } from "@/shared/components";
import { TriggerNameLink } from "@/shared/components/links";
import {
  renderPrincipal,
  ResourceView,
  useResourceView,
  type Filter,
  type FilterMatchMode,
} from "@/shared/components/ResourceView";
import { useAgentDirectory } from "@/shared/domain/agents/hooks";
import {
  eventLabel,
  isSchedule,
  readableCron,
  TARGET_TYPE_LABELS,
  TriggerTargetType,
  type TriggerTargetSpec,
  type TriggerV2Record,
} from "@/shared/domain/triggers";
import {
  useDeleteTriggerV2,
  useSetTriggerV2Enabled,
  useTriggersV2,
} from "@/shared/domain/triggers/hooks";
import { Toggle, ToggleGroup } from "@/shared/foundation/primitives";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";
import {
  cellStyles,
  createColumnHelper,
  DataTable,
  deriveLoadingState,
  FLEX_FILL_COL_DEF,
  renderDashOrValue,
  type ColDef,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { ErrorMonitoringService } from "@/shared/services";

import {
  TRIGGER_FILTER_DEFINITIONS,
  triggerFiresOn,
  triggerMatchesFilters,
  type FiresOnFilter,
} from "./trigger/filterTriggers";
import styles from "./Triggers.module.css";

/** URL search param carrying the quick "fires on" filter, so the view is linkable. */
const FIRES_ON_PARAM = "fires";

const NO_FILTERS: readonly Filter[] = [];

function parseFiresOn(raw: string | null): FiresOnFilter {
  return raw === "events" || raw === "schedule" ? raw : "all";
}

function targetSummary(
  target: TriggerTargetSpec,
  agentNames: ReadonlyMap<string, string>,
): string {
  switch (target.type) {
    case TriggerTargetType.InvokeAction:
      return `${TARGET_TYPE_LABELS[target.type]}: ${target.action.name}`;
    case TriggerTargetType.StartAgent:
      return `${TARGET_TYPE_LABELS[target.type]}: ${
        agentNames.get(target.agent_id) ?? target.agent_id
      }`;
    case TriggerTargetType.SendSlackMessage:
      return TARGET_TYPE_LABELS[target.type];
  }
}

/**
 * Triggers record their author as a bare email, while every other resource
 * list records a `type:id` principal. `renderPrincipal` reads the qualified
 * form, so qualify a bare email here rather than letting it fall through as
 * an untyped value and lose its link to the user's page.
 */
function qualifyPrincipal(modifiedBy: string | undefined): string {
  if (!modifiedBy) {
    return "";
  }
  return modifiedBy.includes(":") ? modifiedBy : `user:${modifiedBy}`;
}

function firesOnSummary(trigger: TriggerV2Record): string {
  return isSchedule(trigger.fires_on)
    ? readableCron(trigger.fires_on.cron)
    : trigger.fires_on.events.map(eventLabel).join(", ");
}

const columnHelper = createColumnHelper<TriggerV2Record>();

/**
 * Every trigger in the org, event-fired and schedule-fired alike, as one
 * filterable table. There is one form to create a trigger, so there is one
 * view to see them; "scheduled" is a filter, not a tab.
 *
 * The list is fetched in one shot, so filtering, sorting, and paging all
 * happen in memory (the same way the skills list does).
 */
export const Triggers: React.FC = () => {
  const { goto } = useNavigation();
  const { trackEvent } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const firesOn = parseFiresOn(searchParams.get(FIRES_ON_PARAM));

  const [errorText, setErrorText] = React.useState<string>("");
  const [pendingDelete, setPendingDelete] =
    React.useState<TriggerV2Record | null>(null);

  const triggersQuery = useTriggersV2();
  const { currentOrganization } = useAuth();
  const agentDirectory = useAgentDirectory(currentOrganization?.org_id);
  const agentNames = React.useMemo(
    () =>
      new Map(
        agentDirectory.agents.map((agent) => [agent.agent_id, agent.name]),
      ),
    [agentDirectory.agents],
  );
  const setEnabled = useSetTriggerV2Enabled();
  const deleteTrigger = useDeleteTriggerV2();

  const view = useResourceView<TriggerV2Record>({
    getRowId: (row) => row.trigger_id,
    initialSort: { columnId: "modified", direction: "desc" },
  });
  const { resetCurrentPage } = view.table;
  const structuredFilters =
    view.filters.type === "structured" ? view.filters.filters : NO_FILTERS;
  const matchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";

  const allTriggers = React.useMemo(
    () => triggersQuery.data ?? [],
    [triggersQuery.data],
  );

  const filteredTriggers = React.useMemo(
    () =>
      allTriggers.filter(
        (trigger) =>
          triggerFiresOn(trigger, firesOn) &&
          triggerMatchesFilters(trigger, structuredFilters, matchMode),
      ),
    [allTriggers, firesOn, structuredFilters, matchMode],
  );

  // The primitive sorts server-side by contract; the list is in memory, so
  // apply the header's sort here before slicing.
  const sortedTriggers = React.useMemo(() => {
    const sort = view.table.sort;
    if (sort === null) {
      return filteredTriggers;
    }
    const factor = sort.direction === "asc" ? 1 : -1;
    const key = (trigger: TriggerV2Record): string =>
      sort.columnId === "name" ? trigger.name : trigger.modified;
    return [...filteredTriggers].sort(
      (a, b) =>
        key(a).localeCompare(key(b), undefined, { sensitivity: "base" }) *
        factor,
    );
  }, [filteredTriggers, view.table.sort]);

  const start = view.table.currentPage * view.table.pageSize;
  const end = start + view.table.pageSize;
  const visibleTriggers = React.useMemo(
    () => sortedTriggers.slice(start, end),
    [sortedTriggers, start, end],
  );

  const selectFiresOn = (next: FiresOnFilter) => {
    setSearchParams(
      (params) => {
        if (next === "all") {
          params.delete(FIRES_ON_PARAM);
        } else {
          params.set(FIRES_ON_PARAM, next);
        }
        return params;
      },
      { replace: true },
    );
    resetCurrentPage();
  };

  const toggleEnabled = React.useCallback(
    (trigger: TriggerV2Record, enabled: boolean) => {
      setErrorText("");
      setEnabled.mutate(
        { name: trigger.name, enabled },
        { onError: (error) => setErrorText(error.message) },
      );
    },
    [setEnabled],
  );

  const performDelete = async () => {
    if (pendingDelete === null) {
      return;
    }
    setErrorText("");
    try {
      await deleteTrigger.mutateAsync({ name: pendingDelete.name });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      ErrorMonitoringService.captureError(err, {
        triggerId: pendingDelete.trigger_id,
      });
      setErrorText(err.message);
    }
  };

  // The primitive hides columns unless a visibility picker opts them in; this
  // list has no picker and shows every column, so flip the default.
  const columns = React.useMemo<ColDef<TriggerV2Record>[]>(() => {
    const baseColumns: ColDef<TriggerV2Record>[] = [
      {
        colId: "name",
        headerName: "Name",
        flex: 2,
        sortable: true,
        valueGetter: (p) => p.data?.name ?? "",
        minWidth: 200,
        cellRenderer: (p: ICellRendererParams<TriggerV2Record>) =>
          p.data ? (
            <TriggerNameLink
              triggerName={p.data.name}
              className={cellStyles.primaryContent}
            />
          ) : null,
      },
      columnHelper.accessor(firesOnSummary, {
        colId: "fires_on",
        headerName: "Fires on",
        flex: 2,
        minWidth: 120,
        sortable: false,
        cellRenderer: renderDashOrValue,
      }),
      columnHelper.accessor(
        (trigger) =>
          trigger.targets
            .map((target) => targetSummary(target, agentNames))
            .join(", "),
        {
          colId: "targets",
          headerName: "Targets",
          flex: 2,
          minWidth: 120,
          sortable: false,
          cellRenderer: renderDashOrValue,
        },
      ),
      columnHelper.timestamp("modified", {
        headerName: "Modified",
        sortable: true,
        variant: "relative",
      }),
      {
        colId: "modified_by",
        headerName: "Modified by",
        minWidth: 150,
        valueGetter: (p) => qualifyPrincipal(p.data?.modified_by),
        cellRenderer: renderPrincipal,
      },
      {
        colId: "enabled",
        headerName: "Enabled",
        flex: 0,
        width: 110,
        cellRenderer: (p: ICellRendererParams<TriggerV2Record>) => {
          const trigger = p.data;
          if (!trigger) {
            return null;
          }
          return (
            <RobotoTooltip
              title={trigger.enabled ? "Disable trigger" : "Enable trigger"}
            >
              <Switch
                size="small"
                checked={trigger.enabled}
                disabled={setEnabled.isPending}
                onChange={(_event, checked) => toggleEnabled(trigger, checked)}
                inputProps={{ "aria-label": `${trigger.name} enabled` }}
              />
            </RobotoTooltip>
          );
        },
      },
      {
        colId: "operations",
        headerName: "",
        flex: 0,
        width: 72,
        sortable: false,
        cellRenderer: (p: ICellRendererParams<TriggerV2Record>) => {
          const trigger = p.data;
          if (!trigger) {
            return null;
          }
          return (
            <div className={styles.operations}>
              <RobotoTooltip title="Delete trigger">
                <span>
                  <RobotoButton
                    variant="ghost"
                    size="sm"
                    className={styles.deleteButton}
                    icon={<Trash2 aria-hidden="true" />}
                    onClick={() => {
                      trackEvent("DeleteTriggerClicked", {
                        triggerId: trigger.trigger_id,
                      });
                      setPendingDelete(trigger);
                    }}
                    disabled={deleteTrigger.isPending}
                    aria-label={`Delete ${trigger.name}`}
                  />
                </span>
              </RobotoTooltip>
            </div>
          );
        },
      },
    ];
    return baseColumns.map((column) => ({ hide: false, ...column }));
  }, [
    agentNames,
    deleteTrigger.isPending,
    setEnabled.isPending,
    toggleEnabled,
    trackEvent,
  ]);

  const loading = deriveLoadingState({
    isLoading: triggersQuery.isLoading,
    isFetching: triggersQuery.isFetching,
  });

  return (
    <>
      <div className={styles.page}>
        {(errorText || triggersQuery.isError) && (
          <Alert severity="error" className={styles.errorAlert}>
            {errorText || triggersQuery.error?.message}
          </Alert>
        )}
        <ResourceView.Root>
          <ResourceView.Actions>
            <ToggleGroup
              size="md"
              className={styles.firesOnToggle}
              aria-label="Fires on"
              value={[firesOn]}
              onValueChange={(next: FiresOnFilter[]) => {
                if (next[0] !== undefined) {
                  selectFiresOn(next[0]);
                }
              }}
            >
              <Toggle value="all">All</Toggle>
              <Toggle value="events">Platform events</Toggle>
              <Toggle value="schedule">Scheduled</Toggle>
            </ToggleGroup>
            <ResourceView.Filters
              filterDefinitions={TRIGGER_FILTER_DEFINITIONS}
              onFilterSelected={view.commit}
            />
            <div className={styles.actionsRight}>
              <RobotoButton
                variant="primary"
                icon={<Plus aria-hidden="true" />}
                onClick={() => goto.createTrigger()}
              >
                Create trigger
              </RobotoButton>
            </div>
          </ResourceView.Actions>
          <ResourceView.ActiveFilters
            filters={view.filters}
            filterDefinitions={TRIGGER_FILTER_DEFINITIONS}
            onRemove={view.removeFilter}
            onUpdate={view.updateFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
          />
          <DataTable<TriggerV2Record>
            table={view.table}
            hasNextPage={end < sortedTriggers.length}
            loading={loading}
            columns={columns}
            defaultColDef={FLEX_FILL_COL_DEF}
            data={visibleTriggers}
            totalRowCount={filteredTriggers.length}
            noRowsContent={
              allTriggers.length === 0 ? (
                <span>
                  No triggers yet. Click <strong>Create trigger</strong> to get
                  started.
                </span>
              ) : (
                <span>No triggers match the current filters.</span>
              )
            }
          />
        </ResourceView.Root>

        <AlertDialog
          dialogOpen={pendingDelete !== null}
          handleClose={() => setPendingDelete(null)}
          dialogTitle={"Delete Trigger"}
          dialogText={
            pendingDelete === null
              ? ""
              : `Are you sure you want to delete ${pendingDelete.name}?`
          }
          dialogAction={performDelete}
        />
      </div>
    </>
  );
};
